import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Truck, 
  Package2, 
  Plus, 
  AlertTriangle,
  Clock,
  Calculator
} from 'lucide-react';

const SupplyModalSimple = ({ isOpen, onClose, selectedCounter }) => {
  const { user, API } = useContext(UserContext);
  const [stockAvailability, setStockAvailability] = useState([]);
  const [selectedStock, setSelectedStock] = useState('');
  const [quantityToSupply, setQuantityToSupply] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentSupplies, setRecentSupplies] = useState([]);

  const counterNames = {
    'pain': 'Pains',
    'viennoiserie': 'Viennoiserie',
    'gateaux': 'Gâteaux'
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, selectedCounter]);

  const fetchData = async () => {
    try {
      const [stockRes, suppliesRes] = await Promise.all([
        axios.get(`${API}/stock/availability`).catch(() => ({ data: [] })),
        axios.get(`${API}/supply?date=${new Date().toISOString().split('T')[0]}&counter=${selectedCounter}`).catch(() => ({ data: [] }))
      ]);
      
      setStockAvailability(stockRes.data.filter(item => item.available_quantity > 0) || []);
      setRecentSupplies(suppliesRes.data || []);
      
    } catch (error) {
      console.error('Error fetching supply data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedStock || !quantityToSupply || !unitCost || !totalCost) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }

      if (parseFloat(quantityToSupply) <= 0 || parseFloat(unitCost) <= 0) {
        toast.error('Les valeurs doivent être positives');
        return;
      }

      setLoading(true);

      await axios.post(`${API}/supply`, {
        stock_item_id: selectedStock,
        stock_item_designation: selectedStock,
        counter: selectedCounter,
        quantity_supplied: parseFloat(quantityToSupply),
        unit_measure: 'kg',
        unit_cost: parseFloat(unitCost),
        total_cost: parseFloat(totalCost),
        notes: notes.trim() || null,
        user_id: user.id
      });

      toast.success(`Approvisionnement effectué pour le guichet ${counterNames[selectedCounter]} !`);
      
      // Reset form
      setSelectedStock('');
      setQuantityToSupply('');
      setUnitCost('');
      setTotalCost('');
      setNotes('');
      
      fetchData();
      
    } catch (error) {
      console.error('Error creating supply:', error);
      toast.error('Erreur lors de l\'approvisionnement');
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate total cost
  useEffect(() => {
    if (quantityToSupply && unitCost) {
      const calculated = parseFloat(quantityToSupply) * parseFloat(unitCost);
      setTotalCost(calculated.toString());
    }
  }, [quantityToSupply, unitCost]);

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Heure invalide';
    }
  };

  const formatCurrency = (amount) => {
    return `${parseFloat(amount || 0).toLocaleString('fr-FR')} F CFA`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Approvisionnement du Jour - {counterNames[selectedCounter]}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulaire d'approvisionnement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Nouvel Approvisionnement</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Stock disponible */}
                <div className="space-y-2">
                  <Label>Stock Disponible :</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                    {stockAvailability.map((item, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded text-sm flex justify-between">
                        <span>{item.designation}</span>
                        <Badge variant="outline">{item.available_quantity} unités</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sélection produit */}
                <div className="space-y-2">
                  <Label>Produit à approvisionner</Label>
                  <select
                    value={selectedStock}
                    onChange={(e) => setSelectedStock(e.target.value)}
                    className="w-full h-10 px-3 border rounded-lg bg-white"
                    required
                  >
                    <option value="">Sélectionnez un produit</option>
                    {stockAvailability.map((item, index) => (
                      <option key={index} value={item.designation}>
                        {item.designation} (Disponible: {item.available_quantity})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantité */}
                <div className="space-y-2">
                  <Label>Quantité à approvisionner</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={quantityToSupply}
                    onChange={(e) => setQuantityToSupply(e.target.value)}
                    placeholder="Ex: 5"
                    required
                  />
                </div>

                {/* Coût unitaire */}
                <div className="space-y-2">
                  <Label>Coût unitaire (F CFA)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={unitCost}
                    onChange={(e) => setUnitCost(e.target.value)}
                    placeholder="Ex: 2500"
                    required
                  />
                </div>

                {/* Coût total */}
                <div className="space-y-2">
                  <Label>Coût total (F CFA)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={totalCost}
                    onChange={(e) => setTotalCost(e.target.value)}
                    placeholder="Calculé automatiquement"
                    required
                  />
                </div>

                {/* Preview */}
                {quantityToSupply && unitCost && (
                  <div className="bg-blue-50 p-3 rounded border">
                    <p className="text-sm"><strong>Résumé :</strong></p>
                    <p className="text-sm">{quantityToSupply} × {unitCost} = <strong>{formatCurrency(totalCost)}</strong></p>
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (optionnel)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes d'approvisionnement"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !selectedStock || !quantityToSupply || !unitCost}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Approvisionnement...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Approvisionner le Guichet
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Approvisionnements récents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Approvisionnements du Jour
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentSupplies.length > 0 ? (
                <div className="space-y-3">
                  {recentSupplies.map((supply, index) => (
                    <div key={supply.id || index} className="p-3 bg-blue-50 rounded border">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{supply.stock_item_designation}</p>
                          <p className="text-sm text-gray-600">
                            {formatTime(supply.time)} • {supply.quantity_supplied} {supply.unit_measure}
                          </p>
                          {supply.notes && (
                            <p className="text-xs text-gray-500">{supply.notes}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge>{supply.quantity_supplied} approvisionnés</Badge>
                          <p className="text-sm font-semibold text-green-600 mt-1">
                            {formatCurrency(supply.total_cost)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucun approvisionnement aujourd'hui</p>
                  <p className="text-xs text-gray-500">pour le guichet {counterNames[selectedCounter]}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplyModalSimple;